import React, { useState, useCallback, useMemo } from 'react';
import {
  func,
} from 'prop-types';
import find from 'lodash/find';
import noop from 'lodash/noop';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import Responsive from '@active/react-utils/components/Responsive';
import Popover from '@active/react-ui/Popover';
import Locale from '@active/react-utils/i18n/Locale';
import RadioGroup from '@active/react-ui/RadioGroup';
import FormGroup from '@active/react-ui/FormGroup';
import FormField from '@active/react-ui/FormField';
import PaymentPlanDetailInfo from './components/PaymentPlanDetailInfo';
import FailPaymentInfo from './components/FailPaymentInfo';
import PaymentOption from './components/PaymentOption';
import { handleKeyboardEvent } from '../../../../../../../shared/utils/commonUtils';

const PaymentOptionsContent = ({
  handleTypeChange,
}) => {
  const {
    locale,
  } = useSelector(state => state.propsData);
  const {
    entities: {
      paymentOptionData,
    },
  } = useSelector(state => state.paymentOptions);

  const {
    paymentOptions,
    installmentPaymentInfoList = [],
    displayInstruction,
    ...rest
  } = paymentOptionData;
  const [ isShowPaymentplanInfo, setShowPaymentplanInfo ] = useState(false);
  const handlePaymentplanShow = useCallback(() => {
    setShowPaymentplanInfo(!isShowPaymentplanInfo);
  }, [ isShowPaymentplanInfo ]);

  const hasPayPastDue = useMemo(() => !!find(paymentOptions, { type: 'PAY_PAST_DUE' }), [ paymentOptions ]);
  const failPaymentOption = useMemo(() => find(paymentOptions, { type: 'PAY_FAILED_PAYMENT' }) || {}, [ paymentOptions ]);
  return (
    <div className="checkout-payment-options cui-mg-t-30">
      <h3 id="payment-options-label" className="third-level-title checkout-payment-options__title">
        <Locale id="checkout.lib.components.paymentOption.title"/>
      </h3>
      {
        installmentPaymentInfoList.length > 0
            && <React.Fragment>
              <Responsive not eqAbove="md">
                <div aria-labelledby='view-payment-plan' aria-expanded={isShowPaymentplanInfo} role='button'
                  tabIndex={0}
                  className="checkout-payment-options__tip"
                  onKeyDown={handleKeyboardEvent(handlePaymentplanShow, [ 'Enter', ' ' ])}
                  onClick={handlePaymentplanShow}>
                  <span id="view-payment-plan">
                    <Locale id="checkout.lib.components.text.viewPaymentPlan"/>
                  </span>
                  <span aria-hidden className={classNames({
                    'ic-triangle-up': isShowPaymentplanInfo,
                    'ic-triangle-down': !isShowPaymentplanInfo,
                  })}/>
                </div>
                {
                  isShowPaymentplanInfo
                && <PaymentPlanDetailInfo
                  installmentPaymentInfoList={installmentPaymentInfoList}
                  locale={locale}/>
                }
              </Responsive>
              <Responsive eqAbove="md">
                <Popover placement="bottom"
                  theme="dark"
                  content={<PaymentPlanDetailInfo
                    installmentPaymentInfoList={installmentPaymentInfoList}
                    locale={locale}/>}>
                  <div
                    aria-haspopup
                    role="button"
                    tabIndex={0}
                    className="checkout-payment-options__tip"
                    data-popover-trigger>
                    <Locale id="checkout.lib.components.text.viewPaymentPlan"/>
                  </div>
                </Popover>
              </Responsive>
            </React.Fragment>
      }
      <FailPaymentInfo {...failPaymentOption} locale={locale}/>
      {displayInstruction
            && <div className="checkout-payment-options__message">
              <div className="checkout-payment-options__message-main">
                <Locale id="checkout.lib.components.text.thankPreviousPayment"/>
              </div>
              <div className="checkout-payment-options__message-sub">
                <Locale id="checkout.lib.components.text.thereNoPaymentDue"/>
              </div>
            </div>
      }
      <FormGroup name="paymentOptionType" className="checkout-payment-options__payment-option-group">
        <FormField
          aria-labelledby="payment-options-label"
          onChange={handleTypeChange}
          as={RadioGroup}>
          {
            paymentOptions.map((paymentOption, index) => <PaymentOption
              key={index}
              locale={locale}
              {...paymentOption}
              hasPayPastDue={hasPayPastDue}
              {...rest}/>)
          }
        </FormField>
      </FormGroup>
    </div>
  );
};

PaymentOptionsContent.propTypes = {
  handleTypeChange: func,
};

PaymentOptionsContent.defaultProps = {
  handleTypeChange: noop,
};

export default PaymentOptionsContent;
